import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Collapse,
  Tabs,
  Switch,
  Card,
  Space,
  Typography,
  Button,
  List,
  Input,
  Popconfirm,
  Tag,
  Tooltip,
  Row,
  Col,
  message,
  Alert,
  Spin,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  GuardrailConfig,
  BusinessRule,
  AdvancedConfig,
  GuardrailPreset,
} from '../../types';
import {
  DEFAULT_GUARDRAILS,
  GUARDRAIL_PRESETS,
  DEFAULT_BUSINESS_RULES,
  GUARDRAIL_CATEGORIES,
} from '../../constants';
import { assistantService } from '../../../assistant-management/services/assistantManagementService';
import { useAssistant } from '../../../../contexts/AssistantContext';
import { AgentUsageMetricsTab } from './AgentUsageMetricsTab';
import './AdvancedConfiguration.css';

const { Panel } = Collapse;
const { TabPane } = Tabs;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface AdvancedConfigurationProps {
  value?: AdvancedConfig;
  onChange?: (config: AdvancedConfig) => void;
  disabled?: boolean;
  mode?: 'edit' | 'create';
}

export const AdvancedConfiguration: React.FC<AdvancedConfigurationProps> = ({
  value,
  onChange,
  disabled = false,
  mode = 'edit',
}) => {
  const { currentAssistant } = useAssistant();
  const [guardrails, setGuardrails] = useState<GuardrailConfig[]>(
    value?.guardrails || DEFAULT_GUARDRAILS
  );
  const [businessRules, setBusinessRules] = useState<BusinessRule[]>(
    value?.businessRules || DEFAULT_BUSINESS_RULES
  );
  const [newRule, setNewRule] = useState('');
  const [loading, setLoading] = useState(false);
  const [businessRulesStrings, setBusinessRulesStrings] = useState<string[]>([]);
  const [editingRule, setEditingRule] = useState<{index: number, text: string} | null>(null);
  const [activePreset, setActivePreset] = useState<string | null>(null);

  // Update local state when value prop changes (from parent component)
  useEffect(() => {
    if (value?.guardrails) {
      setGuardrails(value.guardrails);
    }
    // Always sync businessRulesStrings from the value prop, including when it's empty.
    // Without this, switching from Agent A (with rules) to Agent B (with no rules in the
    // chatbot object) leaves Agent A's rules in local state, which then propagates back to
    // the parent via the notify-parent effect before the /business-rules API fetch completes.
    if (value?.businessRules !== undefined) {
      setBusinessRules(value.businessRules);
      setBusinessRulesStrings(value.businessRules.map(rule => rule.rule));
    }
  }, [value?.guardrails, value?.businessRules]);

  // Use refs to track previous values and prevent infinite loops.
  // Initialize them to the serialized form of the initial state so the first
  // run of the notify-parent effect (below) doesn't see a phantom change and
  // overwrite the parent's `advancedConfig` with empty arrays before the
  // dedicated /chatbot/{id}/business-rules fetch has a chance to populate
  // `businessRulesStrings`. Without this seed, a Save during the load window
  // wipes any pre-existing rules in DynamoDB.
  const previousGuardrailsRef = useRef<string>(
    JSON.stringify(guardrails.map(g => ({ id: g.id, enabled: g.enabled })))
  );
  const previousBusinessRulesRef = useRef<string>(JSON.stringify([] as string[]));

  // Notify parent component when local state changes
  useEffect(() => {
    if (onChange) {
      // Serialize current state to compare with previous
      const currentGuardrailsStr = JSON.stringify(guardrails.map(g => ({ id: g.id, enabled: g.enabled })));
      const currentBusinessRulesStr = JSON.stringify(businessRulesStrings);

      // Only notify parent if state has actually changed
      if (
        currentGuardrailsStr !== previousGuardrailsRef.current ||
        currentBusinessRulesStr !== previousBusinessRulesRef.current
      ) {
        // Convert businessRulesStrings to BusinessRule objects format
        const businessRulesObjects: BusinessRule[] = businessRulesStrings.map((rule, index) => ({
          id: `local-${Date.now()}-${index}`,
          rule: rule,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        }));

        onChange({
          guardrails,
          businessRules: businessRulesObjects,
        });

        // Update refs
        previousGuardrailsRef.current = currentGuardrailsStr;
        previousBusinessRulesRef.current = currentBusinessRulesStr;
      }
    }
  }, [guardrails, businessRulesStrings, onChange]);

  // Load business rules from API when component mounts or assistant changes
  useEffect(() => {
    const loadBusinessRules = async () => {
      if (currentAssistant?.id) {
        try {
          setLoading(true);
          const response = await assistantService.getBusinessRules(currentAssistant.id);

          // Handle both string array and object array formats
          if (Array.isArray(response.businessRules)) {
            if (response.businessRules.length > 0 && typeof response.businessRules[0] === 'string') {
              // If it's an array of strings, use it directly
              setBusinessRulesStrings(response.businessRules);
            } else if (response.businessRules.length > 0 && typeof response.businessRules[0] === 'object') {
              // If it's an array of objects, extract the rule property
              const rulesStrings = response.businessRules.map((rule: any) =>
                typeof rule === 'object' && rule.rule ? rule.rule : String(rule)
              );
              setBusinessRulesStrings(rulesStrings);
            } else {
              setBusinessRulesStrings([]);
            }
          } else {
            setBusinessRulesStrings([]);
          }
        } catch (error) {
          setBusinessRulesStrings([]);
        } finally {
          setLoading(false);
        }
      }
    };

    loadBusinessRules();
  }, [currentAssistant?.id]);


  // Function to detect which preset matches current guardrails configuration
  const detectActivePreset = (currentGuardrails: GuardrailConfig[]): string | null => {
    for (const preset of GUARDRAIL_PRESETS) {
      const presetEnabledIds = preset.config.map(c => c.id);
      const currentEnabledIds = currentGuardrails.filter(g => g.enabled).map(g => g.id);
      
      // Check if the enabled guards match exactly (only checking enabled/disabled state)
      const sameLength = presetEnabledIds.length === currentEnabledIds.length;
      const sameGuards = presetEnabledIds.every(id => currentEnabledIds.includes(id)) &&
                        currentEnabledIds.every(id => presetEnabledIds.includes(id));
      
      if (sameLength && sameGuards) {
        return preset.id;
      }
    }
    return null;
  };

  // Update active preset when guardrails change
  useEffect(() => {
    const activePresetId = detectActivePreset(guardrails);
    setActivePreset(activePresetId);
  }, [guardrails]);

  const handleGuardrailChange = (guardId: string, field: keyof GuardrailConfig, value: any) => {
    setGuardrails(prev =>
      prev.map(guard =>
        guard.id === guardId ? { ...guard, [field]: value } : guard
      )
    );
  };

  const applyPreset = (preset: GuardrailPreset) => {
    // Get the IDs of guards that should be enabled in this preset
    const enabledGuardIds = preset.config.map(p => p.id);

    // Update all guardrails based on the preset (only enabled/disabled state)
    const updatedGuardrails = DEFAULT_GUARDRAILS.map(guard => ({
      ...guard,
      enabled: enabledGuardIds.includes(guard.id)
    }));

    setGuardrails(updatedGuardrails);
    setActivePreset(preset.id);
  };

  const resetToDefaults = () => {
    setGuardrails(DEFAULT_GUARDRAILS);
    setBusinessRules(DEFAULT_BUSINESS_RULES);
    setActivePreset(null); // No preset is active when using defaults
  };

  const addBusinessRule = () => {
    if (!newRule.trim()) {
      message.warning('Please enter a business rule');
      return;
    }

    // Add rule to local state only (will be saved when user saves the configuration)
    setBusinessRulesStrings(prev => [...prev, newRule.trim()]);
    setNewRule('');
    message.success('Business rule added (will be saved when you save the configuration)');
  };

  const removeBusinessRule = (index: number) => {
    // Remove rule from local state only (will be saved when user saves the configuration)
    setBusinessRulesStrings(prev => prev.filter((_, i) => i !== index));
    message.success('Business rule removed (changes will be saved when you save the configuration)');
  };

  const startEditingRule = (index: number, rule: string) => {
    setEditingRule({ index, text: rule });
  };

  const cancelEditingRule = () => {
    setEditingRule(null);
  };

  const saveEditedRule = () => {
    if (!editingRule) {
      message.error('Cannot edit the rule');
      return;
    }

    if (!editingRule.text.trim()) {
      message.warning('The rule cannot be empty');
      return;
    }

    // Update rule in local state only (will be saved when user saves the configuration)
    const updatedRules = [...businessRulesStrings];
    updatedRules[editingRule.index] = editingRule.text.trim();
    setBusinessRulesStrings(updatedRules);

    setEditingRule(null);
    message.success('Business rule updated (changes will be saved when you save the configuration)');
  };

  const getCategoryColor = (category: string) => {
    return GUARDRAIL_CATEGORIES[category as keyof typeof GUARDRAIL_CATEGORIES]?.color || '#666';
  };

  const getCategoryLabel = (category: string) => {
    return GUARDRAIL_CATEGORIES[category as keyof typeof GUARDRAIL_CATEGORIES]?.label || category;
  };

  const groupedGuardrails = guardrails.reduce((acc, guard) => {
    if (!acc[guard.category]) {
      acc[guard.category] = [];
    }
    acc[guard.category].push(guard);
    return acc;
  }, {} as Record<string, GuardrailConfig[]>);

  const renderGuardrailCard = (guard: GuardrailConfig) => (
    <Card
      key={guard.id}
      size="small"
      style={{ marginBottom: 12 }}
      bodyStyle={{ padding: 16 }}
    >
      <Row align="middle" justify="space-between">
        <Col flex="auto">
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space size="small" align="center">
              <Switch
                checked={guard.enabled}
                onChange={(checked) => handleGuardrailChange(guard.id, 'enabled', checked)}
                disabled={disabled}
                size="small"
              />
              <Text strong style={{ fontSize: '14px' }}>{guard.name}</Text>
            </Space>
            <Text type="secondary" style={{ fontSize: '12px', wordBreak: 'break-word', lineHeight: '1.5', marginLeft: 24 }}>
              {guard.description}
            </Text>
          </Space>
        </Col>
      </Row>
    </Card>
  );

  return (
    <div className="advanced-configuration">
      <Collapse
        defaultActiveKey={['advanced']}
        style={{ marginTop: 16 }}
      >
      <Panel
        header={
              <Text strong>Advanced Configurations</Text>
        }
        key="advanced"
        style={{
          border: '1px solid var(--ant-colorSplit)',
          borderRadius: '8px',
        }}
      >
        <Tabs defaultActiveKey="ethics">
          <TabPane
            tab="Behavior Ethics"
            key="ethics"
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {/* Presets Section */}
              <Card size="small" title="Preset Configurations">
                <Space wrap>
                  {GUARDRAIL_PRESETS.map((preset) => (
                    <Tooltip 
                      key={preset.id}
                      title={`${preset.description}${activePreset === preset.id ? ' (Currently Active)' : ''}`}
                      placement="top"
                    >
                      <Button
                        className="Preset-Button"
                        size="small"
                        type={activePreset === preset.id ? 'primary' : 'default'}
                        onClick={() => applyPreset(preset)}
                        disabled={disabled}
                        style={{
                          ...(activePreset === preset.id && {
                            backgroundColor: 'var(--ant-colorSecondary)',
                            borderColor: 'var(--ant-colorSecondary)',
                            color: 'white'
                          })
                        }}
                      >
                        {preset.name}
                        {activePreset === preset.id && ' ✓'}
                      </Button>
                    </Tooltip>
                  ))}
                  <Tooltip title="Reset all guardrails to their default settings" placement="top">
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={resetToDefaults}
                      disabled={disabled}
                      className="Preset-Button"
                    >
                      Restore Defaults
                    </Button>
                  </Tooltip>
                </Space>
                <Paragraph type="secondary" style={{ paddingTop: 8, fontSize: '12px' }}>
                  Quick preset configurations: <strong>Concise</strong> (fast pattern-based guards only), 
                  <strong>Balanced</strong> (security + privacy + critical content guards), 
                  <strong>Maximum</strong> (all guardrails active for complete protection)
                </Paragraph>
              </Card>

              {/* Guardrails by Category */}
              {Object.entries(groupedGuardrails).map(([category, guards]) => (
                <Card
                  key={category}
                  size="small"
                  title={
                    <Space>
                      <Text strong>{getCategoryLabel(category)}</Text>
                      <Tag color={getCategoryColor(category)} style={{ color: 'var(--ant-colorSecondary)', border: '1px solid var(--ant-colorSecondary)', borderRadius: '8px' }}>
                        {guards.filter(g => g.enabled).length}/{guards.length}
                      </Tag>
                    </Space>
                  }
                >
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {guards.map(renderGuardrailCard)}
                  </Space>
                </Card>
              ))}

              {/* Performance Info */}
              <Alert
                message="Performance Information"
                description={
                  <div>
                    <Text>Active guards: {guardrails.filter(g => g.enabled).length}</Text>
                    <br />
                    <Text>LLM guards: {guardrails.filter(g => g.enabled && g.requiresLLM).length}</Text>
                    <br />
                    <Text>Fast guards: {guardrails.filter(g => g.enabled && !g.requiresLLM).length}</Text>
                  </div>
                }
                type="info"
                className="Performance-Information"
              />
            </Space>
          </TabPane>

          <TabPane
            tab="Business Rules"
            key="business"
          >
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              {/* Add New Rule */}
              <Card size="small" title="Add New Rule">
                <Space.Compact style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', justifyContent: 'center', alignItems: 'center'}}>
                  <TextArea
                    value={newRule}
                    onChange={(e) => setNewRule(e.target.value)}
                    placeholder="Enter a new business rule..."
                    disabled={disabled || loading}
                    rows={3}
                    maxLength={10000}
                    showCount
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    style={{ width: '200px'}}
                    onClick={addBusinessRule}
                    disabled={disabled || !newRule.trim() || loading}
                    loading={loading}
                  >
                    Add
                  </Button>
                </Space.Compact>
              </Card>

              {/* Business Rules List */}
              <Card size="small" title={`Business Rules (${businessRulesStrings.length})`}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Spin size="large" />
                  </div>
                ) : (
                  <List
                    dataSource={businessRulesStrings}
                    renderItem={(rule, index) => (
                      <List.Item
                        actions={[
                          editingRule?.index === index ? (
                            <Space>
                              <Button
                                type="text"
                                icon={<SaveOutlined />}
                                onClick={saveEditedRule}
                                disabled={disabled || loading}
                                size="small"
                                title="Save"
                              />
                              <Button
                                type="text"
                                icon={<ReloadOutlined />}
                                onClick={cancelEditingRule}
                                disabled={disabled || loading}
                                size="small"
                                title="Cancel"
                              />
                            </Space>
                          ) : (
                            <Space>
                              <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => startEditingRule(index, rule)}
                                disabled={disabled || loading}
                                size="small"
                                title="Edit"
                              />
                              <Popconfirm
                                title="Delete this rule?"
                                description="This action cannot be undone"
                                onConfirm={() => removeBusinessRule(index)}
                                okText="Yes"
                                cancelText="No"
                              >
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  disabled={disabled || loading}
                                  size="small"
                                  title="Delete"
                                />
                              </Popconfirm>
                            </Space>
                          )
                        ]}
                      >
                        <List.Item.Meta
                          title={
                            editingRule?.index === index ? (
                              <TextArea
                                value={editingRule.text}
                                onChange={(e) => setEditingRule({ ...editingRule, text: e.target.value })}
                                rows={2}
                                style={{ marginTop: 8 }}
                                placeholder="Edit business rule..."
                                maxLength={10000}
                                showCount
                              />
                            ) : (
                              rule
                            )
                          }
                          description={`Rule #${index + 1}`}
                        />
                      </List.Item>
                    )}
                    locale={{
                      emptyText: 'No business rules configured',
                    }}
                  />
                )}
              </Card>
            </Space>
          </TabPane>
          <TabPane
            tab="Uso de LLM"
            key="usage"
          >
            <AgentUsageMetricsTab chatbotId={currentAssistant?.id} />
          </TabPane>
        </Tabs>
      </Panel>
    </Collapse>
    </div>
  );
};